import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import { useLocation } from 'react-router';
import { USER_TYPES } from '@medflex/utils/constants';
import { ClockIcon } from '@heroicons/react/24/outline';
import useUserProfile from '../../../../hooks/useUserProfile';
import PhoneOpeningHoursEdit, { dayByIndex } from './PhoneOpeningHoursEdit';
import usePhoneOpeningHours from '../../../../hooks/usePhoneOpeningHours';

const SettingsBoxLayout = ({ children }) => {
  const { t } = useTranslation('physician', {
    keyPrefix: 'InquiryManagementModule.PhoneOpeningHours',
  });

  return (
    <div className="flex flex-row space-x-2 items-start">
      <span className="hidden sm:block">
        <ClockIcon className="h-8 w-8 stroke-1" />
      </span>
      <div className="flex flex-col space-y-4 overflow-x-hidden">
        <div className="font-semibold">{t('headline')}</div>
        <p className="text-slate-600 text-sm mb-4">{t('description')}</p>
        {children}
      </div>
    </div>
  );
};

const PhoneOpeningHoursModule = () => {
  const { t } = useTranslation('physician', {
    keyPrefix: 'InquiryManagementModule.PhoneOpeningHours',
  });
  const openingHoursAnchor = useRef(null);
  const { hash } = useLocation();
  const { userProfile } = useUserProfile();
  const { phoneOpeningHours, createOrEditOpeningHours, errors } = usePhoneOpeningHours();
  const [isEditing, setIsEditing] = useState(false);

  const isPhysician = [USER_TYPES.PHYSICIAN].includes(userProfile.type);

  const handleSubmit = useCallback(
    async data => {
      try {
        await createOrEditOpeningHours(data);
        setIsEditing(false);
        showSuccessToast(t('form.toasts.save_successful'));
      } catch (e) {
        showAlertToast(t('form.toasts.save_failed'));
      }
    },
    [t, createOrEditOpeningHours],
  );

  useEffect(() => {
    if (hash.includes('openingHours') || isEditing) {
      openingHoursAnchor.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash, isEditing]);

  if (errors?.load) {
    return (
      <div className="mb-5" ref={openingHoursAnchor}>
        <SettingsBox className="text-slate-900">
          <SettingsBoxLayout>
            <p className="text-slate-600 text-sm mb-4">{t('error_loading')}</p>
          </SettingsBoxLayout>
        </SettingsBox>
      </div>
    );
  }

  return (
    <div className="mb-5" ref={openingHoursAnchor}>
      {isEditing && (
        <SettingsBox className="text-slate-900">
          <SettingsBoxLayout>
            <div className="flex flex-col space-y-5 overflow-x-auto">
              <PhoneOpeningHoursEdit
                defaultValues={phoneOpeningHours}
                onSubmit={handleSubmit}
                onCancel={() => setIsEditing(false)}
              />
            </div>
          </SettingsBoxLayout>
        </SettingsBox>
      )}
      {!isEditing && (
        <SettingsBox onEdit={() => setIsEditing(true)} canEdit={isPhysician}>
          <SettingsBoxLayout>
            {!phoneOpeningHours?.active && (
              <p className="text-slate-600 text-sm mb-4">{t('empty')}</p>
            )}
            <div className="flex flex-col space-y-4 sm:space-y-2 w-full">
              {phoneOpeningHours?.active &&
                phoneOpeningHours?.weekdays?.map(({ active, slots }, index) => (
                  <div
                    key={dayByIndex(index)}
                    className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-6"
                  >
                    <p className="font-semibold min-w-28">
                      {t(`form.weekdays.${dayByIndex(index)}`)}
                    </p>
                    {active ? (
                      slots.map(({ startTime, endTime }) => (
                        <div key={startTime} className="flex flex-row items-center space-x-6">
                          {startTime} – {endTime}
                        </div>
                      ))
                    ) : (
                      <span className="text-slate-500">{t('form.closed')}</span>
                    )}
                  </div>
                ))}
            </div>
          </SettingsBoxLayout>
        </SettingsBox>
      )}
    </div>
  );
};

export default PhoneOpeningHoursModule;
